import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LocationAutocomplete } from "./LocationAutocomplete";
import { useLanguage } from "../contexts/LanguageContext";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Relation type configuration with visual styles
const RELATION_CONFIG = {
    CRUSH: { labelKey: "orbit.relation_crush", emoji: "💘", color: "#E91E63", glow: "rgba(233, 30, 99, 0.3)", borderGlow: true },
    PARTNER: { labelKey: "orbit.relation_partner", emoji: "💕", color: "#9C27B0", glow: "rgba(212, 175, 55, 0.25)", borderGlow: true },
    EX: { labelKey: "orbit.relation_ex", emoji: "💔", color: "#78909C", glow: "rgba(244, 67, 54, 0.2)", borderGlow: true, isDark: true },
    FRIEND: { labelKey: "orbit.relation_friend", emoji: "🤝", color: "#4CAF50", glow: "rgba(76, 175, 80, 0.15)", borderGlow: false },
    FAMILY: { labelKey: "orbit.relation_family", emoji: "👨‍👩‍👧", color: "#FF9800", glow: "rgba(255, 152, 0, 0.15)", borderGlow: false },
    ENEMY: { labelKey: "orbit.relation_enemy", emoji: "⚔️", color: "#607D8B", glow: "rgba(244, 67, 54, 0.25)", borderGlow: true, isDark: true },
};

// Zodiac sign symbols and elements
const ZODIAC_DATA = {
    Aries: { symbol: "♈", element: "fire" },
    Taurus: { symbol: "♉", element: "earth" },
    Gemini: { symbol: "♊", element: "air" },
    Cancer: { symbol: "♋", element: "water" },
    Leo: { symbol: "♌", element: "fire" },
    Virgo: { symbol: "♍", element: "earth" },
    Libra: { symbol: "♎", element: "air" },
    Scorpio: { symbol: "♏", element: "water" },
    Sagittarius: { symbol: "♐", element: "fire" },
    Capricorn: { symbol: "♑", element: "earth" },
    Aquarius: { symbol: "♒", element: "air" },
    Pisces: { symbol: "♓", element: "water" },
};

// Mock data for preview when orbit is empty
const MOCK_PROFILES = [
    {
        id: "mock-1",
        name: "Luna Starweaver",
        relation_type: "CRUSH",
        birth_date: "1995-03-21",
        birth_time: "14:30",
        birth_time_unknown: false,
        sun_sign: "Aries",
        moon_sign: "Cancer",
        rising_sign: "Libra",
        compatibility_score: 78,
        compatibility_level: "Strong Connection"
    },
    {
        id: "mock-2",
        name: "Marcus",
        relation_type: "EX",
        birth_date: "1992-08-15",
        birth_time: null,
        birth_time_unknown: true,
        sun_sign: "Leo",
        moon_sign: "Scorpio",
        rising_sign: null,
        compatibility_score: 42,
        compatibility_level: "Growth Opportunity"
    },
    {
        id: "mock-3",
        name: "Sophie Chen",
        relation_type: "FRIEND",
        birth_date: "1994-12-03",
        birth_time: "09:15",
        birth_time_unknown: false,
        sun_sign: "Sagittarius",
        moon_sign: "Gemini",
        rising_sign: "Virgo",
        compatibility_score: 85,
        compatibility_level: "Cosmic Soulmates"
    },
    {
        id: "mock-4",
        name: "The Rival",
        relation_type: "ENEMY",
        birth_date: "1991-06-22",
        birth_time: null,
        birth_time_unknown: true,
        sun_sign: "Cancer",
        moon_sign: "Aries",
        rising_sign: null,
        compatibility_score: 28,
        compatibility_level: "Challenging Terrain"
    },
];

const getConfig = (type) => RELATION_CONFIG[type] || RELATION_CONFIG.FRIEND;
const getZodiac = (sign) => ZODIAC_DATA[sign] || { symbol: "?", element: "unknown" };

export function OrbitSection({ userData, isAuthenticated }) {
    const { t } = useLanguage();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showMockData, setShowMockData] = useState(false);
    
    const [retrying, setRetrying] = useState(false);
    
    const fetchProfiles = useCallback(async (retryCount = 0) => {
        // Only fetch if user is authenticated - prevents 401 errors for guests
        if (!isAuthenticated) {
            setProfiles([]);
            setLoading(false);
            return;
        }
        
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/orbit`, { 
                credentials: "include",
                headers
            });
            if (res.ok) {
                const data = await res.json();
                setProfiles(data.profiles || []);
                setRetrying(false);
            } else if (res.status === 401) {
                // Token expired or invalid - silently continue
                setProfiles([]);
                setRetrying(false);
            } else if (res.status === 503 && retryCount < 3) {
                // Service temporarily unavailable - retry with exponential backoff
                setRetrying(true);
                const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
                console.debug(`[Orbit] 503 error, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
                setTimeout(() => fetchProfiles(retryCount + 1), delay);
                return;
            } else {
                setRetrying(false);
            }
        } catch (err) {
            // Network error - retry once
            if (retryCount < 1) {
                setRetrying(true);
                setTimeout(() => fetchProfiles(retryCount + 1), 1000);
                return;
            }
            console.debug("[Orbit] Could not fetch profiles:", err.message);
            setRetrying(false);
        }
        setLoading(false);
    }, [isAuthenticated]);
    
    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
    
    const handleAddProfile = () => {
        setEditingProfile(null);
        setShowAddModal(true);
    };
    
    const handleEditProfile = (profile) => {
        if (profile.id.startsWith("mock-")) return; // Can't edit mock data
        setEditingProfile(profile);
        setShowAddModal(true);
    };
    
    const handleDeleteProfile = async (profileId) => {
        if (profileId.startsWith("mock-")) return; // Can't delete mock data
        if (!window.confirm(t('dashboard.remove_orbit_confirm'))) return;
        try {
            const headers = await getAuthHeaders();
            await fetch(`${API_URL}/api/orbit/${profileId}`, { 
                method: "DELETE", 
                credentials: "include",
                headers
            });
            setProfiles(prev => prev.filter(p => p.id !== profileId));
            setSelectedProfile(null);
        } catch (err) {
            console.error("Failed to delete profile:", err);
        }
    };
    
    const handleSaveProfile = async (profileData) => {
        try {
            const isEdit = !!editingProfile;
            const url = isEdit ? `${API_URL}/api/orbit/${editingProfile.id}` : `${API_URL}/api/orbit`;
            const method = isEdit ? "PUT" : "POST";
            const headers = await getAuthHeaders();
            
            const res = await fetch(url, {
                method,
                headers,
                credentials: "include",
                body: JSON.stringify(profileData)
            });
            
            if (res.ok) {
                const saved = await res.json();
                if (isEdit) {
                    setProfiles(prev => prev.map(p => p.id === saved.id ? saved : p));
                } else {
                    setProfiles(prev => [...prev, saved]);
                    setShowMockData(false); // Hide mock data when real profile is added
                }
                setShowAddModal(false);
                setEditingProfile(null);
            }
        } catch (err) {
            console.error("Failed to save profile:", err);
        }
    };
    
    const handleAnalyzeBond = (profile) => {
        setSelectedProfile(profile);
    };
    
    // Determine which profiles to display
    const displayProfiles = profiles.length > 0 ? profiles : (showMockData ? MOCK_PROFILES : []);
    
    // Handle personalized orbit invite share
    const handleInviteShare = async () => {
        const userSunSign = userData?.sun_sign || userData?.zodiacSign || "cosmic soul";
        const shareText = `I am a ${userSunSign}. My orbit is chaotic. Dare to align or collide? Check our compatibility on Astral Belonging: ${window.location.origin}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Join My Cosmic Orbit',
                    text: shareText,
                    url: window.location.origin
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    // Fallback to clipboard
                    copyToClipboard(shareText);
                }
            }
        } else {
            copyToClipboard(shareText);
        }
    };
    
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Could add a toast here
    };
    
    return (
        <motion.div 
            className="w-full mb-6 px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.25em]">
                        {t('dashboard.your_orbit')}
                    </h2>
                    <p className="text-[9px] text-foreground/30 mt-0.5">
                        {profiles.length > 0 ? `${profiles.length} ${profiles.length > 1 ? t('dashboard.connections') : t('dashboard.connection')}` : t('dashboard.cosmic_connections')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Invite Button */}
                    <button
                        onClick={handleInviteShare}
                        className="text-[9px] font-inter uppercase tracking-wider px-3 py-1.5 rounded-full transition-all hover:scale-105"
                        style={{ 
                            background: 'rgba(147, 112, 219, 0.1)',
                            border: '1px solid rgba(147, 112, 219, 0.3)',
                            color: 'rgba(147, 112, 219, 0.9)'
                        }}
                        data-testid="invite-orbit-btn"
                    >
                        ✦ {t('dashboard.invite')}
                    </button>
                    {/* Add Soul Button */}
                    <button
                        onClick={handleAddProfile}
                        className="text-[9px] font-inter uppercase tracking-wider px-3 py-1.5 rounded-full transition-all hover:scale-105"
                        style={{ 
                            background: 'rgba(212, 175, 55, 0.1)',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            color: '#D4AF37'
                        }}
                        data-testid="add-orbit-profile"
                    >
                        + {t('dashboard.add_soul')}
                    </button>
                </div>
            </div>
            
            {/* Profiles Grid */}
            {loading || retrying ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <motion.div 
                        className="w-10 h-10 rounded-full border-2 border-primary/30"
                        style={{ borderTopColor: '#D4AF37' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    {retrying && (
                        <motion.p 
                            className="text-xs text-primary/60 font-inter"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            ✨ {t('common.stars_aligning') || 'The Stars are Aligning...'}
                        </motion.p>
                    )}
                </div>
            ) : displayProfiles.length === 0 ? (
                <EmptyOrbitState onAdd={handleAddProfile} onShowMock={() => setShowMockData(true)} t={t} />
            ) : (
                <>
                    {/* Mock Data Banner */}
                    {showMockData && profiles.length === 0 && (
                        <motion.div 
                            className="mb-4 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <span className="text-[10px] text-primary/60">{t('dashboard.preview_mode')} - {t('dashboard.preview_mode_desc')}</span>
                            <button 
                                onClick={() => setShowMockData(false)} 
                                className="text-[9px] text-primary/40 hover:text-primary"
                            >
                                {t('dashboard.hide')}
                            </button>
                        </motion.div>
                    )}
                    
                    {/* Cards Grid - Tarot Card Layout */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {displayProfiles.map((profile, idx) => (
                            <OrbitCard
                                key={profile.id}
                                profile={profile}
                                index={idx}
                                isMock={profile.id.startsWith("mock-")}
                                onEdit={() => handleEditProfile(profile)}
                                onDelete={() => handleDeleteProfile(profile.id)}
                                onAnalyzeBond={() => handleAnalyzeBond(profile)}
                                t={t}
                            />
                        ))}
                    </div>
                </>
            )}
            
            {/* Add/Edit Modal */}
            <OrbitFormModal
                isOpen={showAddModal}
                onClose={() => { setShowAddModal(false); setEditingProfile(null); }}
                onSave={handleSaveProfile}
                editingProfile={editingProfile}
            />
            
            {/* Compatibility Modal */}
            <CompatibilityModal
                isOpen={!!selectedProfile}
                onClose={() => setSelectedProfile(null)}
                profile={selectedProfile}
                userName={userData?.name}
                isMock={selectedProfile?.id?.startsWith("mock-")}
                t={t}
            />
        </motion.div>
    );
}

function EmptyOrbitState({ onAdd, onShowMock, t }) {
    return (
        <motion.div 
            className="text-center py-12 px-6 rounded-2xl relative overflow-hidden"
            style={{ 
                background: 'linear-gradient(135deg, rgba(12,10,18,0.9) 0%, rgba(20,18,28,0.8) 100%)',
                border: '1px dashed rgba(212, 175, 55, 0.15)'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
            {/* Decorative stars */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(12)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 rounded-full bg-primary/30"
                        style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
                        animate={{ opacity: [0.2, 0.6, 0.2] }}
                        transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
                    />
                ))}
            </div>
            
            <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
                <span className="text-5xl">🌌</span>
            </motion.div>
            <h3 className="mt-4 font-cinzel text-lg text-primary/80">{t('dashboard.your_orbit_awaits')}</h3>
            <p className="text-xs text-foreground/40 mt-2 max-w-xs mx-auto">
                {t('dashboard.orbit_description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
                <button
                    onClick={onAdd}
                    className="text-xs font-inter uppercase tracking-wider px-5 py-2.5 rounded-lg transition-all hover:scale-105"
                    style={{ 
                        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
                        border: '1px solid rgba(212, 175, 55, 0.4)',
                        color: '#D4AF37'
                    }}
                >
                    {t('dashboard.add_soul')}
                </button>
                <button
                    onClick={onShowMock}
                    className="text-xs font-inter text-foreground/40 hover:text-foreground/60 transition-colors"
                >
                    {t('dashboard.preview_samples')}
                </button>
            </div>
        </motion.div>
    );
}

/**
 * OrbitCard - Tarot-style card for displaying a connection
 * Features: Name, Relationship badge, Big Three (Sun/Moon/Rising), Analyze Bond button
 */
function OrbitCard({ profile, index, isMock, onEdit, onDelete, onAnalyzeBond, t }) {
    const config = getConfig(profile.relation_type);
    const sunZodiac = getZodiac(profile.sun_sign);
    const moonZodiac = getZodiac(profile.moon_sign);
    const risingZodiac = getZodiac(profile.rising_sign);
    
    const hasRising = !profile.birth_time_unknown && profile.rising_sign;
    const hasCompatibility = profile.compatibility_score != null;
    
    // Determine border glow based on relationship type
    const getBorderStyle = () => {
        if (config.isDark) {
            return {
                border: `1px solid rgba(244, 67, 54, 0.3)`,
                boxShadow: `0 0 20px ${config.glow}, inset 0 0 30px rgba(0,0,0,0.5)`
            };
        }
        if (config.borderGlow) {
            return {
                border: `1px solid ${config.color}40`,
                boxShadow: `0 0 25px ${config.glow}, inset 0 0 30px rgba(0,0,0,0.5)`
            };
        }
        return {
            border: '1px solid rgba(212, 175, 55, 0.15)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        };
    };
    
    return (
        <motion.div
            className="relative group cursor-pointer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            onClick={onAnalyzeBond}
            data-testid={`orbit-card-${profile.id}`}
        >
            {/* Tarot Card Container - ~2:3 aspect ratio */}
            <div 
                className="relative rounded-xl overflow-hidden"
                style={{
                    background: 'linear-gradient(180deg, rgba(20,18,28,0.98) 0%, rgba(12,10,18,0.99) 100%)',
                    aspectRatio: '2/3',
                    ...getBorderStyle()
                }}
            >
                {/* Mock Badge */}
                {isMock && (
                    <div className="absolute top-0 left-0 right-0 py-0.5 text-center text-[7px] font-inter uppercase tracking-wider bg-primary/10 text-primary/50 z-20">
                        {t('dashboard.preview_mode')}
                    </div>
                )}
                
                {/* Card Background Glow */}
                <div 
                    className="absolute inset-0 opacity-30"
                    style={{
                        background: `radial-gradient(ellipse at top, ${config.color}30 0%, transparent 70%)`
                    }}
                />
                
                {/* Corner Decorations */}
                <div className="absolute top-2 left-2 w-4 h-4 border-t border-l opacity-30" style={{ borderColor: config.color }} />
                <div className="absolute top-2 right-2 w-4 h-4 border-t border-r opacity-30" style={{ borderColor: config.color }} />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l opacity-30" style={{ borderColor: config.color }} />
                <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r opacity-30" style={{ borderColor: config.color }} />
                
                {/* Card Content */}
                <div className="relative z-10 h-full flex flex-col p-3">
                    
                    {/* HEADER: Relationship Badge */}
                    <div className="flex items-center justify-center mb-2">
                        <span 
                            className="text-[9px] font-inter uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1"
                            style={{ 
                                background: `${config.color}15`,
                                border: `1px solid ${config.color}30`,
                                color: config.color
                            }}
                        >
                            <span>{config.emoji}</span>
                            <span>{t(config.labelKey)}</span>
                        </span>
                    </div>
                    
                    {/* NAME */}
                    <h3 
                        className="font-cinzel text-center text-sm truncate px-1"
                        style={{ 
                            color: config.isDark ? 'rgba(255,255,255,0.7)' : '#F5D76E'
                        }}
                        title={profile.name}
                    >
                        {profile.name}
                    </h3>
                    
                    {/* SUN SIGN (Main) */}
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <motion.div
                            className="text-4xl mb-1"
                            style={{ 
                                color: config.color,
                                textShadow: `0 0 20px ${config.color}60`
                            }}
                            animate={{ 
                                textShadow: [
                                    `0 0 20px ${config.color}40`,
                                    `0 0 30px ${config.color}70`,
                                    `0 0 20px ${config.color}40`
                                ]
                            }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            {sunZodiac.symbol}
                        </motion.div>
                        <span className="text-[10px] text-foreground/60">{profile.sun_sign || t('dashboard.unknown')}</span>
                    </div>
                    
                    {/* THE BIG THREE ROW */}
                    <div className="mb-3">
                        <p className="text-[8px] font-inter uppercase tracking-wider text-center text-foreground/30 mb-1.5">
                            {t('dashboard.the_big_three')}
                        </p>
                        <div className="flex justify-center gap-3">
                            {/* Sun */}
                            <BigThreeIcon 
                                label="☉"
                                sign={profile.sun_sign}
                                symbol={sunZodiac.symbol}
                                color={config.color}
                                tooltip="Sun Sign"
                                t={t}
                            />
                            
                            {/* Moon */}
                            <BigThreeIcon 
                                label="☽"
                                sign={profile.moon_sign}
                                symbol={moonZodiac.symbol}
                                color={config.color}
                                tooltip="Moon Sign"
                                t={t}
                            />
                            
                            {/* Rising - Lock if unknown */}
                            <BigThreeIcon 
                                label="↑"
                                sign={hasRising ? profile.rising_sign : null}
                                symbol={hasRising ? risingZodiac.symbol : "🔒"}
                                color={config.color}
                                locked={!hasRising}
                                tooltip={hasRising ? "Ascendant Sign" : "Birth time required"}
                                t={t}
                            />
                        </div>
                    </div>
                    
                    {/* Compatibility Score (if calculated) */}
                    {hasCompatibility && (
                        <div className="mb-2 text-center">
                            <span 
                                className="text-lg font-cinzel"
                                style={{ 
                                    color: profile.compatibility_score >= 65 ? '#4CAF50' : 
                                           profile.compatibility_score >= 40 ? '#FFC107' : '#FF5722'
                                }}
                            >
                                {profile.compatibility_score}%
                            </span>
                            <span className="text-[8px] text-foreground/40 block">{profile.compatibility_level}</span>
                        </div>
                    )}
                    
                    {/* ANALYZE BOND BUTTON */}
                    <button
                        onClick={(e) => { e.stopPropagation(); onAnalyzeBond(); }}
                        className="w-full py-2 rounded-lg text-[10px] font-inter uppercase tracking-wider transition-all hover:scale-[1.02]"
                        style={{
                            background: `linear-gradient(135deg, ${config.color}20 0%, ${config.color}10 100%)`,
                            border: `1px solid ${config.color}40`,
                            color: config.color
                        }}
                        data-testid={`analyze-bond-${profile.id}`}
                    >
                        {hasCompatibility ? t('dashboard.view_bond') : t('dashboard.analyze_bond')}
                    </button>
                </div>
                
                {/* Edit/Delete Actions (show on hover) */}
                {!isMock && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-20">
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-[10px] hover:bg-primary/20 transition-colors"
                            title={t('orbit.edit_connection')}
                        >
                            ✏️
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-[10px] hover:bg-red-500/20 transition-colors"
                            title={t('common.remove')}
                        >
                            🗑️
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

/**
 * BigThreeIcon - Sun/Moon/Ascendant display with lock state
 * NEW LAYOUT: SIGN NAME on top, ZODIAC ICON in middle, PLACEMENT LABEL at bottom
 */
function BigThreeIcon({ label, sign, symbol, color, locked, tooltip, t }) {
    // Map label to translated full text
    const getLabelText = (lbl) => {
        if (lbl === '☉') return t ? t('dashboard.sun_label') : 'SUN';
        if (lbl === '☽') return t ? t('dashboard.moon_label') : 'MOON';
        if (lbl === '↑') return t ? t('dashboard.ascendant_label') : 'ASCENDANT';
        return lbl;
    };
    const fullLabel = getLabelText(label);
    
    return (
        <div 
            className={`flex flex-col items-center min-w-[50px] ${locked ? 'opacity-50' : ''}`}
            title={tooltip}
        >
            {/* SIGN NAME - Top */}
            <span 
                className="text-[8px] font-inter font-medium uppercase tracking-wider mb-0.5"
                style={{ color: locked ? 'rgba(150,150,150,0.5)' : color }}
            >
                {locked ? "?" : (sign || "?")}
            </span>
            
            {/* ZODIAC ICON - Middle */}
            <div 
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ 
                    background: locked ? 'rgba(100,100,100,0.1)' : `${color}15`,
                    border: `1px solid ${locked ? 'rgba(100,100,100,0.2)' : `${color}25`}`
                }}
            >
                <span 
                    className="text-xl"
                    style={{ 
                        color: locked ? 'rgba(150,150,150,0.4)' : color,
                        filter: locked ? 'grayscale(100%)' : 'none'
                    }}
                >
                    {symbol}
                </span>
            </div>
            
            {/* PLACEMENT LABEL - Bottom */}
            <span 
                className="text-[6px] font-inter uppercase tracking-wider mt-0.5"
                style={{ color: 'rgba(255,255,255,0.3)' }}
            >
                {fullLabel}
            </span>
        </div>
    );
}

/**
 * OrbitFormModal - Add/Edit connection form
 * UPDATED: Now captures lat/lng from Google Places API and fetches timezone
 */
function OrbitFormModal({ isOpen, onClose, onSave, editingProfile }) {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: "",
        relation_type: "FRIEND",
        birth_date: "",
        birth_time: "",
        birth_place: { display: "", lat: null, lng: null, utc_offset_minutes: null },
        birth_time_unknown: false,
        notes: ""
    });
    const [saving, setSaving] = useState(false);
    const [timezoneId, setTimezoneId] = useState(null);
    const [timezoneLoading, setTimezoneLoading] = useState(false);
    
    // Fetch timezone when coordinates and birth date are available
    useEffect(() => {
        const fetchTimezone = async () => {
            if (!formData.birth_place.lat || !formData.birth_place.lng || !formData.birth_date) {
                setTimezoneId(null);
                return;
            }
            
            setTimezoneLoading(true);
            try {
                const headers = await getAuthHeaders();
                const birthDate = new Date(formData.birth_date + 'T12:00:00Z');
                const timestamp = Math.floor(birthDate.getTime() / 1000);
                
                const response = await fetch(
                    `${API_URL}/api/orbit/timezone?lat=${formData.birth_place.lat}&lng=${formData.birth_place.lng}&timestamp=${timestamp}`,
                    { headers, credentials: 'include' }
                );
                
                if (response.ok) {
                    const data = await response.json();
                    setTimezoneId(data.timeZoneId);
                    console.log('🌍 Timezone fetched:', data.timeZoneId);
                }
            } catch (err) {
                console.error('Timezone fetch error:', err);
            }
            setTimezoneLoading(false);
        };
        
        fetchTimezone();
    }, [formData.birth_place.lat, formData.birth_place.lng, formData.birth_date]);
    
    useEffect(() => {
        if (editingProfile) {
            setFormData({
                name: editingProfile.name || "",
                relation_type: editingProfile.relation_type || "FRIEND",
                birth_date: editingProfile.birth_date || "",
                birth_time: editingProfile.birth_time || "",
                birth_place: {
                    display: editingProfile.birth_place || "",
                    lat: editingProfile.birth_place_lat || null,
                    lng: editingProfile.birth_place_lng || null,
                    utc_offset_minutes: editingProfile.utc_offset_minutes || null
                },
                birth_time_unknown: editingProfile.birth_time_unknown || false,
                notes: editingProfile.notes || ""
            });
            setTimezoneId(editingProfile.timezone_id || null);
        } else {
            setFormData({
                name: "",
                relation_type: "FRIEND",
                birth_date: "",
                birth_time: "",
                birth_place: { display: "", lat: null, lng: null, utc_offset_minutes: null },
                birth_time_unknown: false,
                notes: ""
            });
            setTimezoneId(null);
        }
    }, [editingProfile, isOpen]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.birth_date) return;
        
        setSaving(true);
        
        // Prepare data with coordinates and timezone
        const submitData = {
            name: formData.name,
            relation_type: formData.relation_type,
            birth_date: formData.birth_date,
            birth_time: formData.birth_time,
            birth_place: formData.birth_place.display || "",
            birth_place_lat: formData.birth_place.lat,
            birth_place_lng: formData.birth_place.lng,
            utc_offset_minutes: formData.birth_place.utc_offset_minutes,
            timezone_id: timezoneId,  // Include timezone from Google Time Zone API
            birth_time_unknown: formData.birth_time_unknown,
            notes: formData.notes
        };
        
        console.log('📍 Saving orbit profile with coordinates and timezone:', submitData);
        
        await onSave(submitData);
        setSaving(false);
    };
    
    const RELATION_TYPES = Object.entries(RELATION_CONFIG).map(([value, config]) => ({
        value,
        ...config
    }));
    
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl"
                            style={{ 
                                background: 'linear-gradient(135deg, rgba(20,18,28,0.98) 0%, rgba(12,10,18,0.99) 100%)',
                                border: '1px solid rgba(212, 175, 55, 0.2)'
                            }}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={e => e.stopPropagation()}
                            data-testid="orbit-form-modal"
                        >
                            {/* Header */}
                            <div className="p-4 border-b border-white/5 flex items-center justify-between">
                                <h3 className="font-cinzel text-lg text-primary">
                                    {editingProfile ? t('orbit.edit_connection') : t('orbit.add_to_orbit')}
                                </h3>
                                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5">
                                    <span className="text-foreground/50">✕</span>
                                </button>
                            </div>
                            
                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-4 space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50 block mb-1">
                                        {t('orbit.their_name')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-primary/20 text-foreground/90 text-sm focus:outline-none focus:border-primary/50"
                                        placeholder={t('common.enter_name')}
                                        required
                                        data-testid="orbit-form-name"
                                    />
                                </div>
                                
                                {/* Relation Type Grid */}
                                <div>
                                    <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50 block mb-2">
                                        {t('orbit.relationship_type')}
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {RELATION_TYPES.map(type => (
                                            <button
                                                key={type.value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, relation_type: type.value }))}
                                                className="px-2 py-2.5 rounded-lg text-center transition-all"
                                                style={{
                                                    background: formData.relation_type === type.value ? `${type.color}20` : 'rgba(0,0,0,0.2)',
                                                    border: `1px solid ${formData.relation_type === type.value ? type.color : 'rgba(255,255,255,0.08)'}`,
                                                    color: formData.relation_type === type.value ? type.color : 'rgba(255,255,255,0.4)'
                                                }}
                                            >
                                                <span className="block text-lg">{type.emoji}</span>
                                                <span className="text-[9px] font-inter">{t(type.labelKey)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Birth Date */}
                                <div>
                                    <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50 block mb-1">
                                        {t('orbit.birth_date')} *
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.birth_date}
                                        onChange={e => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                                        className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-primary/20 text-foreground/90 text-sm focus:outline-none focus:border-primary/50"
                                        required
                                        data-testid="orbit-form-birthdate"
                                    />
                                </div>
                                
                                {/* Birth Time with Unknown checkbox */}
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50">
                                            {t('orbit.birth_time')}
                                        </label>
                                        <label className="flex items-center gap-1.5 text-[9px] text-foreground/50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.birth_time_unknown}
                                                onChange={e => setFormData(prev => ({ 
                                                    ...prev, 
                                                    birth_time_unknown: e.target.checked, 
                                                    birth_time: e.target.checked ? "" : prev.birth_time 
                                                }))}
                                                className="w-3 h-3 accent-primary"
                                            />
                                            {t('dashboard.unknown')}
                                        </label>
                                    </div>
                                    {!formData.birth_time_unknown && (
                                        <input
                                            type="time"
                                            value={formData.birth_time}
                                            onChange={e => setFormData(prev => ({ ...prev, birth_time: e.target.value }))}
                                            className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-primary/20 text-foreground/90 text-sm focus:outline-none focus:border-primary/50"
                                            data-testid="orbit-form-birthtime"
                                        />
                                    )}
                                    {formData.birth_time_unknown && (
                                        <p className="text-[9px] text-foreground/30 mt-1 flex items-center gap-1">
                                            🔒 {t('orbit.birth_time_locked')}
                                        </p>
                                    )}
                                </div>
                                
                                {/* Birth Place with Google Places Autocomplete */}
                                <div>
                                    <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50 block mb-1">
                                        {t('orbit.birth_place')}
                                    </label>
                                    <div className="rounded-lg bg-black/30 border border-primary/20 px-1">
                                        <LocationAutocomplete
                                            value={formData.birth_place}
                                            onChange={(locationData) => setFormData(prev => ({ ...prev, birth_place: locationData }))}
                                            placeholder="City, Country"
                                        />
                                    </div>
                                    {formData.birth_place.lat && (
                                        <div className="mt-1.5 space-y-0.5">
                                            <p className="text-[8px] text-primary/30">
                                                📍 {t('orbit.coordinates_captured')}: {formData.birth_place.lat.toFixed(4)}, {formData.birth_place.lng.toFixed(4)}
                                            </p>
                                            {timezoneLoading ? (
                                                <p className="text-[8px] text-primary/40 flex items-center gap-1">
                                                    <span className="animate-pulse">⏳</span> Detecting timezone...
                                                </p>
                                            ) : timezoneId ? (
                                                <p className="text-[8px] text-green-400/70">
                                                    🌍 Timezone: {timezoneId}
                                                </p>
                                            ) : formData.birth_date ? null : (
                                                <p className="text-[8px] text-amber-400/50">
                                                    ⚠️ Enter birth date to detect timezone
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Notes */}
                                <div>
                                    <label className="text-[10px] font-inter uppercase tracking-wider text-primary/50 block mb-1">
                                        {t('orbit.notes')}
                                    </label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-primary/20 text-foreground/90 text-sm focus:outline-none focus:border-primary/50 resize-none"
                                        rows={2}
                                        placeholder={t('orbit.notes_placeholder')}
                                    />
                                </div>
                                
                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={saving || !formData.name || !formData.birth_date}
                                    className="w-full py-3 rounded-lg font-inter text-sm uppercase tracking-wider transition-all disabled:opacity-50 hover:scale-[1.01]"
                                    style={{ 
                                        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
                                        border: '1px solid rgba(212, 175, 55, 0.4)',
                                        color: '#D4AF37'
                                    }}
                                    data-testid="orbit-form-submit"
                                >
                                    {saving ? t('orbit.adding_to_orbit') : (editingProfile ? t('orbit.update_connection') : t('orbit.add_to_orbit'))}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

/**
 * CompatibilityModal - "Cosmic Synastry Report" with streaming AI diagnosis
 * Features: Synergy Meter, Ruthless Oracle AI, "Text Them" trigger
 */
function CompatibilityModal({ isOpen, onClose, profile, userName, isMock, t }) {
    const [data, setData] = useState(null);
    const [diagnosis, setDiagnosis] = useState("");
    const [loading, setLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    
    useEffect(() => {
        if (isOpen && profile) {
            if (isMock) {
                // Mock data for preview
                setData({
                    user_name: userName || "You",
                    target_name: profile.name,
                    relation_type: profile.relation_type,
                    overall_score: profile.compatibility_score || 65,
                    compatibility_level: profile.compatibility_level || "Compatible",
                    scores: { love: 72, communication: 68, passion: 75, growth: 58 },
                    sun_signs: { user: "Gemini", partner: profile.sun_sign },
                    moon_signs: { user: "Pisces", partner: profile.moon_sign },
                    venus_signs: { user: "Taurus", partner: "Libra" },
                    aspect_summary: { harmonious: 5, dynamic: 4, challenging: 3 },
                    birth_time_unknown: profile.birth_time_unknown
                });
                setDiagnosis(t('orbit.preview_diagnosis'));
                setLoading(false);
            } else {
                fetchDiagnosis();
            }
        } else {
            setData(null);
            setDiagnosis("");
            setError(null);
        }
    }, [isOpen, profile, isMock, userName, t]);
    
    const fetchDiagnosis = async () => {
        setLoading(true);
        setError(null);
        setDiagnosis("");
        setIsStreaming(false);
        
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/orbit/${profile.id}/diagnosis/stream`, {
                method: "POST",
                credentials: "include",
                headers
            });
            
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Failed to generate diagnosis");
            }
            
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let diagnosisText = "";
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n");
                
                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        try {
                            const parsed = JSON.parse(line.slice(6));
                            if (parsed.type === "metadata") {
                                setData(parsed);
                                setLoading(false);
                                setIsStreaming(true);
                            } else if (parsed.type === "text") {
                                diagnosisText += parsed.content;
                                setDiagnosis(diagnosisText);
                            } else if (parsed.type === "done") {
                                setIsStreaming(false);
                            }
                        } catch (e) {}
                    }
                }
            }
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };
    
    const copyBirthTimeText = () => {
        navigator.clipboard.writeText(t('orbit.text_them_prompt') + " 🌙");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    const config = profile ? getConfig(profile.relation_type) : { color: '#D4AF37', emoji: '✨' };
    const scoreColor = data?.overall_score >= 65 ? '#4CAF50' : 
                       data?.overall_score >= 40 ? '#FFC107' : '#FF5722';
    
    // Get relation-specific title
    const getReportTitle = () => {
        switch (profile?.relation_type) {
            case "EX": return t('orbit.haunted_file_report');
            case "ENEMY": return t('orbit.tactical_scouting_report');
            case "CRUSH": return t('orbit.target_acquisition_brief');
            case "PARTNER": return t('orbit.bond_analysis_report');
            case "FAMILY": return t('orbit.karmic_bond_analysis');
            default: return t('orbit.synastry_report');
        }
    };
    
    return (
        <AnimatePresence>
            {isOpen && profile && (
                <>
                    <motion.div
                        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl"
                            style={{ 
                                background: 'linear-gradient(135deg, rgba(15,13,20,0.99) 0%, rgba(8,6,12,1) 100%)',
                                border: `1px solid ${config.color}30`,
                                boxShadow: `0 0 60px ${config.glow || 'rgba(212,175,55,0.15)'}, inset 0 0 80px rgba(0,0,0,0.5)`
                            }}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={e => e.stopPropagation()}
                            data-testid="synastry-modal"
                        >
                            {/* Header */}
                            <div className="p-4 border-b border-white/5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[9px] font-inter uppercase tracking-[0.2em] text-primary/40 mb-1">
                                            {getReportTitle()}
                                        </p>
                                        <h3 className="font-cinzel text-xl" style={{ color: config.color }}>
                                            {profile.name}
                                        </h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span 
                                                className="text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1"
                                                style={{ background: `${config.color}15`, border: `1px solid ${config.color}30`, color: config.color }}
                                            >
                                                {config.emoji} {t(config.labelKey)}
                                            </span>
                                            <span className="text-[10px] text-foreground/40">
                                                {profile.sun_sign}
                                            </span>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={onClose} 
                                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-foreground/50">✕</span>
                                    </button>
                                </div>
                            </div>
                            
                            {/* Content */}
                            <div className="p-5">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <motion.div 
                                            className="w-16 h-16 rounded-full border-2 border-primary/20"
                                            style={{ borderTopColor: config.color, borderRightColor: config.color }}
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                                        />
                                        <p className="mt-4 text-sm text-foreground/50 italic font-inter">
                                            {t('orbit.ruthless_coach_analyzing')}
                                        </p>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-10">
                                        <span className="text-4xl">⚠️</span>
                                        <p className="mt-4 text-sm text-foreground/60">{error}</p>
                                        <button 
                                            onClick={fetchDiagnosis} 
                                            className="mt-4 px-5 py-2 text-xs uppercase tracking-wider rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors"
                                        >
                                            {t('orbit.retry_analysis')}
                                        </button>
                                    </div>
                                ) : data ? (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        
                                        {/* THE SYNERGY METER */}
                                        <div className="text-center mb-6">
                                            <div className="relative inline-block">
                                                <svg className="w-36 h-36 transform -rotate-90">
                                                    {/* Background circle */}
                                                    <circle 
                                                        cx="72" cy="72" r="62" 
                                                        fill="none" 
                                                        stroke="rgba(255,255,255,0.05)" 
                                                        strokeWidth="12" 
                                                    />
                                                    {/* Progress circle */}
                                                    <motion.circle
                                                        cx="72" cy="72" r="62" 
                                                        fill="none" 
                                                        stroke={scoreColor}
                                                        strokeWidth="12"
                                                        strokeDasharray={`${2 * Math.PI * 62}`}
                                                        strokeLinecap="round"
                                                        initial={{ strokeDashoffset: 2 * Math.PI * 62 }}
                                                        animate={{ strokeDashoffset: 2 * Math.PI * 62 * (1 - data.overall_score / 100) }}
                                                        transition={{ duration: 1.5, ease: "easeOut" }}
                                                        style={{ filter: `drop-shadow(0 0 8px ${scoreColor})` }}
                                                    />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <motion.span 
                                                        className="text-4xl font-cinzel font-bold"
                                                        style={{ color: scoreColor }}
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
                                                    >
                                                        {data.overall_score}%
                                                    </motion.span>
                                                    <span className="text-[10px] text-foreground/40 uppercase tracking-wider">{t('orbit.match')}</span>
                                                </div>
                                            </div>
                                            <p className="mt-3 font-cinzel text-lg" style={{ color: scoreColor }}>
                                                {data.compatibility_level}
                                            </p>
                                            <p className="text-xs text-foreground/30 mt-0.5">
                                                {data.user_name || userName || "You"} ↔ {data.target_name || profile.name}
                                            </p>
                                        </div>
                                        
                                        {/* Category Scores - Compact */}
                                        <div className="grid grid-cols-4 gap-2 mb-5">
                                            <MiniScoreBar label={t('orbit.score_love')} score={data.scores?.love} emoji="💕" />
                                            <MiniScoreBar label={t('orbit.score_talk')} score={data.scores?.communication} emoji="💬" />
                                            <MiniScoreBar label={t('orbit.score_fire')} score={data.scores?.passion} emoji="🔥" />
                                            <MiniScoreBar label={t('orbit.score_growth')} score={data.scores?.growth} emoji="🌱" />
                                        </div>
                                        
                                        {/* Sign Comparison Row */}
                                        <div className="flex justify-center gap-4 mb-5 p-3 rounded-xl bg-black/30">
                                            <SignBadge label="☉" sign={data.sun_signs?.partner} title={t('dashboard.sun_label')} />
                                            <SignBadge label="☽" sign={data.moon_signs?.partner} title={t('dashboard.moon_label')} />
                                            <SignBadge 
                                                label="↑" 
                                                sign={data.birth_time_unknown ? null : "?"} 
                                                title={t('dashboard.ascendant_label')}
                                                locked={data.birth_time_unknown}
                                            />
                                        </div>
                                        
                                        {/* THE AI DIAGNOSIS */}
                                        <div 
                                            className="p-4 rounded-xl mb-4"
                                            style={{ 
                                                background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)',
                                                border: '1px solid rgba(212, 175, 55, 0.1)'
                                            }}
                                        >
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-lg">🔮</span>
                                                <p className="text-[10px] font-inter uppercase tracking-[0.15em] text-primary/50">
                                                    {t('orbit.ruthless_coach_diagnosis')}
                                                </p>
                                            </div>
                                            
                                            {diagnosis ? (
                                                <p className="text-sm font-inter text-foreground/85 leading-relaxed">
                                                    {diagnosis}
                                                    {isStreaming && (
                                                        <motion.span
                                                            className="inline-block w-2 h-4 ml-1 bg-primary/60"
                                                            animate={{ opacity: [1, 0, 1] }}
                                                            transition={{ duration: 0.8, repeat: Infinity }}
                                                        />
                                                    )}
                                                </p>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <motion.div
                                                        className="flex gap-1"
                                                        animate={{ opacity: [0.3, 1, 0.3] }}
                                                        transition={{ duration: 1.5, repeat: Infinity }}
                                                    >
                                                        <span className="w-2 h-2 rounded-full bg-primary/50" />
                                                        <span className="w-2 h-2 rounded-full bg-primary/50" />
                                                        <span className="w-2 h-2 rounded-full bg-primary/50" />
                                                    </motion.div>
                                                    <span className="text-xs text-foreground/40 italic">
                                                        {t('orbit.channeling_wisdom')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Aspect Summary - Mini */}
                                        <div className="flex justify-center gap-8 text-center mb-4">
                                            <div>
                                                <span className="block text-lg text-green-500">{data.aspect_summary?.harmonious || 0}</span>
                                                <span className="text-[9px] text-foreground/30 uppercase">{t('orbit.aspect_sync')}</span>
                                            </div>
                                            <div>
                                                <span className="block text-lg text-yellow-500">{data.aspect_summary?.dynamic || 0}</span>
                                                <span className="text-[9px] text-foreground/30 uppercase">{t('orbit.aspect_spark')}</span>
                                            </div>
                                            <div>
                                                <span className="block text-lg text-red-400">{data.aspect_summary?.challenging || 0}</span>
                                                <span className="text-[9px] text-foreground/30 uppercase">{t('orbit.aspect_friction')}</span>
                                            </div>
                                        </div>
                                        
                                        {/* "TEXT THEM" TRIGGER - Shows when birth time is unknown */}
                                        {data.birth_time_unknown && (
                                            <motion.div 
                                                className="mt-4 p-3 rounded-xl flex items-center justify-between gap-3"
                                                style={{ 
                                                    background: 'rgba(255, 193, 7, 0.08)',
                                                    border: '1px solid rgba(255, 193, 7, 0.2)'
                                                }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.5 }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">⚠️</span>
                                                    <div>
                                                        <p className="text-[10px] font-inter text-yellow-500/90 font-medium">Data Incomplete</p>
                                                        <p className="text-[9px] text-foreground/40">Rising Sign is locked. Get their birth time!</p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={copyBirthTimeText}
                                                    className="px-3 py-1.5 rounded-lg text-[9px] font-inter uppercase tracking-wider transition-all hover:scale-105"
                                                    style={{ 
                                                        background: copied ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 193, 7, 0.15)',
                                                        border: `1px solid ${copied ? 'rgba(76, 175, 80, 0.4)' : 'rgba(255, 193, 7, 0.3)'}`,
                                                        color: copied ? '#4CAF50' : '#FFC107'
                                                    }}
                                                >
                                                    {copied ? `${t('common.copied')} ✓` : t('common.copy_text')}
                                                </button>
                                            </motion.div>
                                        )}
                                        
                                        {/* Mock Banner */}
                                        {isMock && (
                                            <div className="mt-4 p-2 rounded-lg bg-primary/5 text-center">
                                                <p className="text-[9px] text-primary/50">
                                                    {t('orbit.preview_ai_note')}
                                                </p>
                                            </div>
                                        )}
                                    </motion.div>
                                ) : null}
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

/**
 * Mini score bar for compact category display
 */
function MiniScoreBar({ label, score, emoji }) {
    const color = score >= 65 ? '#4CAF50' : score >= 40 ? '#FFC107' : '#FF5722';
    const displayScore = score || 0;
    
    return (
        <div className="text-center">
            <span className="text-lg block mb-1">{emoji}</span>
            <div className="h-1 rounded-full bg-white/10 overflow-hidden mb-1">
                <motion.div
                    className="h-full rounded-full"
                    style={{ background: color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${displayScore}%` }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                />
            </div>
            <span className="text-[9px] text-foreground/40">{label}</span>
        </div>
    );
}

/**
 * Sign badge for the Big Three display
 */
/**
 * SignBadge - Used in Compatibility Modal
 * NEW LAYOUT: SIGN NAME on top, ZODIAC ICON in middle, PLACEMENT LABEL at bottom
 */
function SignBadge({ label, sign, title, locked }) {
    // Map title to uppercase label
    const labelText = title.toUpperCase();
    
    return (
        <div className={`text-center min-w-[60px] ${locked ? 'opacity-50' : ''}`} title={title}>
            {/* SIGN NAME - Top */}
            <span 
                className="text-[9px] font-inter font-medium uppercase tracking-wider block mb-1"
                style={{ color: locked ? 'rgba(150,150,150,0.5)' : '#D4AF37' }}
            >
                {locked ? "?" : (sign || "?")}
            </span>
            
            {/* ZODIAC ICON - Middle */}
            <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto"
                style={{ 
                    background: locked ? 'rgba(100,100,100,0.1)' : 'rgba(212,175,55,0.1)',
                    border: `1px solid ${locked ? 'rgba(100,100,100,0.2)' : 'rgba(212,175,55,0.2)'}`
                }}
            >
                <span className="text-xl" style={{ color: locked ? '#666' : '#D4AF37' }}>
                    {locked ? "🔒" : (ZODIAC_DATA[sign]?.symbol || label)}
                </span>
            </div>
            
            {/* PLACEMENT LABEL - Bottom */}
            <span 
                className="text-[7px] font-inter uppercase tracking-wider mt-1 block"
                style={{ color: 'rgba(255,255,255,0.3)' }}
            >
                {labelText}
            </span>
        </div>
    );
}

export default OrbitSection;
